/**
 * The bootstrap wizard's model (2.J deliverable 10), pure: four independent steps, each with its
 * own state (`editing → previewing → previewed → applying → applied`; a 409 returns to `editing`
 * with the fixed line, a 422 shows its issues at their rows), the Stepper's statuses, and the
 * past-sessions rows. Next and Back only move; no step waits for another's Apply.
 */
import {
  BOOTSTRAP_SESSIONS_MAX,
  type BootstrapPreviewV1,
  type BootstrapSessionsV1,
} from '@codex/contracts';

export const STEP_IDS = ['sessions', 'now', 'status', 'ledger'] as const;
export type StepId = (typeof STEP_IDS)[number];

export const STEP_LABELS: Readonly<Record<StepId, string>> = {
  sessions: 'Past sessions',
  now: 'NOW',
  status: 'Statuses',
  ledger: 'Known and played',
};

export type StepStatus = 'complete' | 'current' | 'upcoming';

/** The Stepper's statuses: the current step, the steps visited so far, the rest still ahead. */
export function stepStatuses(
  current: StepId,
  visited: ReadonlySet<StepId>,
): Record<StepId, StepStatus> {
  const out = {} as Record<StepId, StepStatus>;
  for (const id of STEP_IDS)
    out[id] = id === current ? 'current' : visited.has(id) ? 'complete' : 'upcoming';
  return out;
}

export function stepAfter(id: StepId): StepId | null {
  return STEP_IDS[STEP_IDS.indexOf(id) + 1] ?? null;
}

export function stepBefore(id: StepId): StepId | null {
  return STEP_IDS[STEP_IDS.indexOf(id) - 1] ?? null;
}

// ----------------------------------------------------------------------------- a step's state

export type Phase = 'editing' | 'previewing' | 'previewed' | 'applying' | 'applied';

export interface Issue {
  /** The row of the request the issue names; null when it names the request as a whole. */
  readonly index: number | null;
  readonly code: string;
}

export interface StepState {
  readonly phase: Phase;
  readonly preview: BootstrapPreviewV1 | null;
  readonly notice: string | null;
  readonly issues: readonly Issue[];
  /** The input changed while the preview was in flight: its answer no longer matches the form. */
  readonly stale: boolean;
}

export const INITIAL_STEP: StepState = {
  phase: 'editing',
  preview: null,
  notice: null,
  issues: [],
  stale: false,
};

export const CONFLICT_TEXT = 'Something changed since the preview. Preview again.';
export const FAILED_TEXT = "That didn't go through. Try again.";
export const NO_ANSWER_TEXT = "The server didn't answer. Try again.";

/** The input changed (or an applied step is edited again): the preview no longer holds. */
export function edited(state: StepState): StepState {
  if (state.phase === 'previewing') return state.stale ? state : { ...state, stale: true };
  return state.phase === 'applying' ? state : INITIAL_STEP;
}

export const previewing = (): StepState => ({ ...INITIAL_STEP, phase: 'previewing' });
/** The answer lands as a preview only when the input was not edited while it was in flight. */
export const previewed = (state: StepState, preview: BootstrapPreviewV1): StepState =>
  state.stale ? INITIAL_STEP : { ...INITIAL_STEP, phase: 'previewed', preview };
export const applying = (state: StepState): StepState =>
  state.phase === 'previewed' ? { ...state, phase: 'applying', notice: null, issues: [] } : state;
export const applied = (): StepState => ({ ...INITIAL_STEP, phase: 'applied' });

export type Failure =
  | { readonly kind: 'conflict' }
  | { readonly kind: 'invalid'; readonly issues: readonly Issue[] }
  | { readonly kind: 'other'; readonly text: string };

/** A failed preview or apply, read from the error's shape (an `ApiError` or a `NetworkError`). */
export function failureOf(error: unknown): Failure {
  const e = (typeof error === 'object' && error !== null ? error : {}) as {
    name?: unknown;
    status?: unknown;
    code?: unknown;
    body?: { details?: { issues?: unknown } };
  };
  if (e.name === 'NetworkError') return { kind: 'other', text: NO_ANSWER_TEXT };
  if (e.status === 409 && e.code === 'row_conflict') return { kind: 'conflict' };
  if (e.status === 422) {
    const raw = e.body?.details?.issues;
    const issues = Array.isArray(raw) ? raw.map(issueOf) : [];
    return { kind: 'invalid', issues };
  }
  return { kind: 'other', text: FAILED_TEXT };
}

function issueOf(raw: unknown): Issue {
  const r = (typeof raw === 'object' && raw !== null ? raw : {}) as {
    path?: unknown;
    code?: unknown;
  };
  const index = Array.isArray(r.path)
    ? r.path.find((p): p is number => typeof p === 'number')
    : undefined;
  return { index: index ?? null, code: typeof r.code === 'string' ? r.code : 'invalid' };
}

/** A failed preview or apply as the step's next state: a 409 returns to editing. */
export function failed(_: StepState, failure: Failure): StepState {
  if (failure.kind === 'conflict') return { ...INITIAL_STEP, notice: CONFLICT_TEXT };
  if (failure.kind === 'invalid') return { ...INITIAL_STEP, issues: failure.issues };
  return { ...INITIAL_STEP, notice: failure.text };
}

/** What a row of the preview writes. */
export function writesOf(preview: BootstrapPreviewV1): number {
  const c = preview.counts;
  return c.create + c.untrash + c.update + c.record;
}

/** Apply is open only on a current preview with a write, and no row that keeps an earlier date. */
export function canApply(state: StepState): boolean {
  const { preview } = state;
  return (
    state.phase === 'previewed' &&
    preview !== null &&
    writesOf(preview) > 0 &&
    !preview.rows.some((r) => r.dateKept === true)
  );
}

export const ISSUE_TEXT: Readonly<Record<string, string>> = {
  duplicate_number: 'This number is already in the list.',
  duplicate_option: 'This option is mapped twice.',
  duplicate_target: 'This target is chosen twice.',
  not_markable: "This can't be marked that way.",
  not_playable: "This type can't be played.",
  not_select: 'This field is not a select field.',
  reference_document: 'Reference pages are never marked.',
  reference_type: 'Reference types are never mapped.',
  session_not_played: 'That session has not been played yet.',
  target_trashed: 'This page is in the trash.',
  unknown_field: 'That field no longer exists.',
  unknown_option: 'That option is not in the field.',
  unknown_session: 'There is no session with that number.',
  unknown_target: 'That target no longer exists.',
  unknown_type: 'That type no longer exists.',
  unstorable_text: "That text can't be stored.",
  date_kept: 'This session keeps its earlier date.',
  nothing_to_apply: 'There is nothing to apply.',
};

export const issueText = (code: string): string => ISSUE_TEXT[code] ?? "This row can't be applied.";

// ------------------------------------------------------------------------- the sessions rows

export interface SessionRow {
  readonly number: number;
  readonly realDate: string;
  readonly title: string;
}

/** The highest number `sessions.list` returns, plus one; 1 when there is none. */
export function nextStart(numbers: readonly (number | null)[]): number {
  return numbers.reduce<number>((top, n) => (n !== null && n > top ? n : top), 0) + 1;
}

/** Rows `start … start + count - 1`; a row keeps what was typed for its number. */
export function sessionRows(
  count: number,
  start: number,
  previous: readonly SessionRow[] = [],
): SessionRow[] {
  const n = Math.min(Math.max(Math.trunc(count) || 1, 1), BOOTSTRAP_SESSIONS_MAX);
  const typed = new Map(previous.map((r) => [r.number, r]));
  return Array.from(
    { length: n },
    (_, i) => typed.get(start + i) ?? { number: start + i, realDate: '', title: '' },
  );
}

export function sessionsRequest(rows: readonly SessionRow[]): BootstrapSessionsV1 {
  return {
    sessions: rows.map((r) => ({
      number: r.number,
      ...(r.realDate === '' ? {} : { realDate: r.realDate }),
      ...(r.title.trim() === '' ? {} : { title: r.title.trim() }),
    })),
  };
}
